import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { getLocation, locations, landmarks } from "@/data/locations";
import { testimonials } from "@/lib/testimonials";
import NotFound from "@/pages/NotFound";
import { MapPin, Check, ArrowRight, Quote, Star } from "lucide-react";

const SITE = "https://betterthroughboxing.com";

export default function LocationDetail() {
  const { slug } = useParams();
  const loc = getLocation(slug);

  useSEO({
    title: loc ? `Boxing Lessons in ${loc.city}, NJ — Private & Virtual Coaching | Better Through Boxing` : "Page not found · Better Through Boxing",
    description: loc ? loc.intro : "This page isn't available.",
    path: loc ? `/boxing-lessons/${slug}` : "/not-found",
    keywords: loc ? [`boxing lessons ${loc.city}`, `boxing coach ${loc.city} nj`, `private boxing trainer ${loc.county}`, "boxing lessons near me", "boxing for beginners nj"] : [],
    noindex: !loc,
  });

  useEffect(() => {
    if (!loc) return;
    const ld = {
      "@context": "https://schema.org", "@type": "Service",
      serviceType: "Private Boxing Lessons",
      provider: { "@type": "LocalBusiness", name: "The Flow State Method LLC", url: SITE, telephone: "+1-732-648-4949" },
      areaServed: { "@type": "City", name: `${loc.city}, New Jersey` },
      description: `Private in-person and virtual boxing lessons serving ${loc.city}, ${loc.county}, New Jersey — ${loc.focus}.`,
      url: `${SITE}/boxing-lessons/${loc.slug}`,
    };
    const el = document.createElement("script");
    el.type = "application/ld+json"; el.id = "location-jsonld"; el.text = JSON.stringify(ld);
    document.getElementById("location-jsonld")?.remove();
    document.head.appendChild(el);
    return () => { document.getElementById("location-jsonld")?.remove(); };
  }, [loc]);

  // Unknown town slug → real 404 (noindex), never a 200 duplicate of the hub.
  if (!loc) return <NotFound />;

  const perks = [
    `1-on-1 in-person sessions for athletes in and around ${loc.city}`,
    "Virtual Zoom coaching if you'd rather train from home",
    "Beginner-friendly — footwork, the jab, and the Core 4 from day one",
    `Built for ${loc.focus} — real technique, not just a sweat session`,
  ];
  const faqs = [
    loc.faq,
    { q: `I've never boxed before — is that okay?`, a: `Absolutely. Most clients start as complete beginners. You'll learn stance, guard, footwork, and the fundamental punches in a relaxed, no-pressure environment right here in ${loc.city}.` },
    { q: `Can I train virtually instead?`, a: `Yes — virtual Zoom coaching is available worldwide and covers the same technique and personal feedback as in-person sessions in ${loc.city}.` },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-8">
        <Link to="/boxing-lessons" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-white mb-6">
          <MapPin className="h-3.5 w-3.5" /> Central NJ service area
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight">Boxing Lessons in {loc.city}, NJ</h1>
        <p className="text-zinc-300 text-base mt-4">{loc.intro}</p>
        <p className="text-zinc-400 text-base mt-3">{loc.about}</p>
        {landmarks[loc.slug] && (
          <p className="text-zinc-400 text-base mt-3 border-l-2 border-[#FF331F] pl-4" data-testid="location-landmark">
            Whether you&rsquo;re near {landmarks[loc.slug]} or on a quiet side street, boxing training in {loc.city} comes right to your door.
          </p>
        )}
        <Link to="/book" className="inline-flex items-center gap-2 mt-6 bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs px-6 py-3" data-testid="location-book-cta">
          Book your first session <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-8">
        <h2 className="font-display text-2xl uppercase tracking-tight mb-4">What you get in {loc.city}</h2>
        <ul className="grid sm:grid-cols-2 gap-3">
          {perks.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-200"><Check className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" /> {p}</li>
          ))}
        </ul>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex gap-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-[#FF331F] text-[#FF331F]" />)}</div>
          <span className="text-sm text-zinc-300 font-semibold">What athletes say</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {testimonials.slice(0, 3).map((t, i) => (
            <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-4">
              <Quote className="h-4 w-4 text-[#FF331F] mb-2" />
              <p className="text-sm text-zinc-200 leading-snug">&ldquo;{t.quote}&rdquo;</p>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-3">— {t.author}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-8">
        <h2 className="font-display text-2xl uppercase tracking-tight mb-4">Boxing in {loc.city} — FAQ</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-5">
              <div className="font-semibold text-white">{f.q}</div>
              <p className="text-sm text-zinc-400 mt-2">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Nearby towns</div>
        <div className="flex flex-wrap gap-2">
          {locations.filter((l) => l.slug !== loc.slug).slice(0, 8).map((l) => (
            <Link key={l.slug} to={`/boxing-lessons/${l.slug}`} className="text-xs border border-[#27272A] px-3 py-1.5 text-zinc-400 hover:border-[#FF331F] hover:text-white">
              {l.city}
            </Link>
          ))}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
